import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion'
import {
  CalculationProcessStepResponse,
  ResolvedProcessStepTarget,
} from 'store/types/assembler'
import { Badge } from '../ui/badge'
import { ExternalLink, MessageSquareText } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts'

const MAX_LABEL_CHARS = 38

const truncateLabel = (label: string): string =>
  label.length > MAX_LABEL_CHARS
    ? label.slice(0, MAX_LABEL_CHARS).trimEnd() + '…'
    : label

interface CustomYAxisTickProps {
  x?: number
  y?: number
  payload?: { value: string }
}

const CustomYAxisTick = ({ x = 0, y = 0, payload }: CustomYAxisTickProps) => {
  const fullName = payload?.value ?? ''
  const displayName = truncateLabel(fullName)
  const isTruncated = fullName.length > MAX_LABEL_CHARS

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={4}
        textAnchor="end"
        fill="hsl(var(--muted-foreground))"
        fontSize={12}
      >
        {isTruncated && <title>{fullName}</title>}
        {displayName}
      </text>
    </g>
  )
}

interface ProcessChainProps {
  processChain: CalculationProcessStepResponse[]
}

const contextGroupLabel = (contextName: string): string => {
  if (contextName.includes('energy')) return 'Energy'
  if (contextName.includes('metals')) return 'Metals'
  if (contextName.includes('polymers')) return 'Polymers'
  if (contextName.includes('composites')) return 'Composites'
  if (contextName.includes('sinters')) return 'Sinters'
  if (contextName.includes('elements')) return 'Elements'
  if (contextName.includes('chemicals')) return 'Chemicals'
  if (contextName.includes('electronics')) return 'Electronics'
  if (contextName.includes('transportation')) return 'Transportation'
  if (contextName.includes('packaging')) return 'Packaging'
  if (contextName.includes('employees')) return 'Employees'
  if (contextName.includes('equipment')) return 'Equipment'
  if (contextName.includes('manufacturing')) return 'Manufacturing'
  if (contextName.startsWith('.materials') || contextName === '.materials')
    return 'Materials'
  if (contextName.startsWith('.production') || contextName === '.production')
    return 'Production'
  if (contextName.startsWith('.logistics') || contextName === '.logistics')
    return 'Logistics'
  if (contextName.startsWith('.components') || contextName === '.components')
    return 'Components'
  return contextName || 'Other'
}

const groupTargetsByContext = (targets: ResolvedProcessStepTarget[]) => {
  const groups = new Map<string, ResolvedProcessStepTarget[]>()
  for (const target of targets ?? []) {
    const label = contextGroupLabel(target.contextName ?? '')
    const list = groups.get(label) ?? []
    list.push(target)
    groups.set(label, list)
  }
  return groups
}

export const ProcessChain = ({ processChain }: ProcessChainProps) => {
  const chartData = processChain.map((step) => ({
    name: step.name,
    emissions: step.totalEmissions ?? 0,
  }))

  const COLORS = [
    'hsl(var(--chart-1))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))',
    'hsl(var(--chart-5))',
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle>Manufacturing Process Chain</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div
          style={{
            height: `${Math.max(processChain.length * 70, 400)}px`,
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical">
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="hsl(var(--border))"
              />
              <XAxis type="number" stroke="hsl(var(--muted-foreground))" />
              <YAxis
                dataKey="name"
                type="category"
                width={300}
                stroke="hsl(var(--muted-foreground))"
                tick={<CustomYAxisTick />}
              />
              <Tooltip
                formatter={(value: number) => value.toFixed(5) + ' kg CO₂e'}
                contentStyle={{
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 'var(--radius)',
                }}
              />
              <Bar dataKey="emissions" radius={[0, 8, 8, 0]}>
                {chartData.map((_entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <Accordion type="single" collapsible className="w-full">
          {processChain.map((step) => (
            <AccordionItem key={step.position} value={`step-${step.position}`}>
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-3 text-left">
                  <Badge variant="outline">{step.position}</Badge>
                  <span className="font-medium">{step.name}</span>
                  {step.comment && (
                    <MessageSquareText
                      className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                      aria-label="This step has a comment"
                    />
                  )}
                  <span className="text-sm text-muted-foreground ml-auto mr-4">
                    {(step.totalEmissions ?? 0).toFixed(5)} kg CO₂e
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="pl-12 space-y-4 pt-4">
                  {step.comment && (
                    <div className="space-y-2">
                      <h4 className="font-semibold text-sm">Comment</h4>
                      <p className="text-sm text-muted-foreground whitespace-pre-line">
                        {step.comment}
                      </p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm">
                      Origin Composition
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground">Biogenic:</span>
                        <span>
                          {(step.emissionOriginComposition?.biogenic ?? 0).toFixed(5)}
                        </span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground">Fossil:</span>
                        <span>
                          {(step.emissionOriginComposition?.fossil ?? 0).toFixed(5)}
                        </span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground">LULUC:</span>
                        <span>
                          {(step.emissionOriginComposition?.luluc ?? 0).toFixed(5)}
                        </span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground">Aircraft:</span>
                        <span>
                          {(step.emissionOriginComposition?.aircraft ?? 0).toFixed(5)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm">
                      Category Breakdown
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">Materials:</span>
                        <div className="font-medium">
                          {(step.emissionsByCategory?.materials ?? 0).toFixed(5)}
                        </div>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Production:</span>
                        <div className="font-medium">
                          {(step.emissionsByCategory?.production ?? 0).toFixed(5)}
                        </div>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Logistics:</span>
                        <div className="font-medium">
                          {(step.emissionsByCategory?.logistics ?? 0).toFixed(5)}
                        </div>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Components:</span>
                        <div className="font-medium">
                          {(step.emissionsByCategory?.components ?? 0).toFixed(5)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {(step.targets?.length ?? 0) > 0 && (
                    <div className="space-y-3 mt-6 pt-4 border-t">
                      <h4 className="font-semibold text-sm">
                        Included Datasets
                      </h4>

                      {[...groupTargetsByContext(step.targets)].map(
                        ([label, items]) => (
                          <div key={label} className="space-y-2">
                            <h5 className="text-xs font-medium text-muted-foreground uppercase">
                              {label}
                            </h5>
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Name</TableHead>
                                  <TableHead>SID / Type</TableHead>
                                  <TableHead>Emissions</TableHead>
                                  <TableHead>Source</TableHead>
                                  <TableHead>Docs</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {items.map((item, idx) => (
                                  <TableRow key={`${item.sid ?? item.name}-${idx}`}>
                                    <TableCell className="font-medium">
                                      <div>{item.customName || item.name}</div>
                                      {item.comment && (
                                        <div className="text-xs text-muted-foreground mt-1">
                                          {item.comment}
                                        </div>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {item.sid ?? item.referenceType}
                                    </TableCell>
                                    <TableCell>
                                      {(item.emissions ?? 0).toFixed(5)}
                                    </TableCell>
                                    <TableCell className="max-w-[160px] truncate">
                                      {item.source || '—'}
                                    </TableCell>
                                    <TableCell>
                                      {item.documentationLink && (
                                        <a
                                          href={item.documentationLink}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center gap-1 text-primary hover:underline"
                                        >
                                          <ExternalLink className="h-3 w-3" />
                                          Docs
                                        </a>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </CardContent>
    </Card>
  )
}
